<template>
    <div class="public-tariff-page">
        <div class="public-tariff-hero">
            <img class="public-tariff-logo" :src="$img('logo.png')" alt="" />
        </div>

        <section class="public-tariff-sheet">
            <header class="public-tariff-sheet-head">
                <h1 class="public-tariff-title">{{ translate('cotizacionTitulo') }}</h1>
                <p class="public-tariff-intro" v-if="settings">{{ translate('cotizacionIntro') }}</p>
            </header>

            <loader :flag="!settings" :customAttribute="{tag: 'circle', children: 0, attribute:'stroke', value: '--main-color'}"></loader>

            <div v-if="settings && settings.modulo_precio_viaje" class="public-tariff-body">
            <div class="public-tariff-field-row">
                <div class="public-tariff-field-main">
                    <div class="public-tariff-label-row">
                        <label class="form-label" for="public_origen">{{ translate('origen') }}</label>
                        <button
                            type="button"
                            class="public-tariff-swap-inline-btn"
                            @click="swapOriginDestination"
                            :aria-label="translate('cotizacionInvertirAria')"
                            :title="translate('cotizacionInvertirAria')"
                        >
                            <span class="public-tariff-swap-icon" aria-hidden="true">⇅</span>
                        </button>
                    </div>
                    <locationAutocomplete
                        :key="'o-' + originRemountKey"
                        id="public_origen"
                        ref="originAutocomplete"
                        :initial-value="originDescription"
                        :placeholder-text="translate('placeholderOrigen')"
                        v-on:direction_change="onOriginChange"
                    />
                </div>
                <div class="public-tariff-field-actions" role="group" :aria-label="translate('origen')">
                    <button
                        type="button"
                        class="btn btn-sm btn-link public-tariff-action-btn"
                        @click="useMyLocationFor('origin')"
                        :aria-label="translate('cotizacionGpsOrigenAria')"
                    >
                        {{ translate('cotizacionAccionGps') }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-sm btn-link public-tariff-action-btn public-tariff-action-map"
                        @click="openMapPicker('origin')"
                        :aria-label="translate('cotizacionMapaOrigenAria')"
                    >
                        {{ translate('cotizacionAccionMapa') }}
                    </button>
                </div>
            </div>

            <div class="public-tariff-field-row">
                <div class="public-tariff-field-main">
                    <div class="public-tariff-label-row">
                        <label class="form-label" for="public_destino">{{ translate('destino') }}</label>
                        <button
                            type="button"
                            class="public-tariff-swap-inline-btn"
                            @click="swapOriginDestination"
                            :aria-label="translate('cotizacionInvertirAria')"
                            :title="translate('cotizacionInvertirAria')"
                        >
                            <span class="public-tariff-swap-icon" aria-hidden="true">⇅</span>
                        </button>
                    </div>
                    <locationAutocomplete
                        :key="'d-' + destRemountKey"
                        id="public_destino"
                        ref="destAutocomplete"
                        :initial-value="destDescription"
                        :placeholder-text="translate('placeholderText')"
                        v-on:direction_change="onDestinationChange"
                    />
                </div>
                <div class="public-tariff-field-actions" role="group" :aria-label="translate('destino')">
                    <button
                        type="button"
                        class="btn btn-sm btn-link public-tariff-action-btn"
                        @click="useMyLocationFor('dest')"
                        :aria-label="translate('cotizacionGpsDestinoAria')"
                    >
                        {{ translate('cotizacionAccionGps') }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-sm btn-link public-tariff-action-btn public-tariff-action-map"
                        @click="openMapPicker('dest')"
                        :aria-label="translate('cotizacionMapaDestinoAria')"
                    >
                        {{ translate('cotizacionAccionMapa') }}
                    </button>
                </div>
            </div>

            <div class="form-input-wrapper" v-if="!settings.deshabilitar_ida_vuelta">
                <input type="checkbox" v-model="esIdaVuelta" id="public_esIdaVuelta" @change="resetEstimateDisplay" />
                <label for="public_esIdaVuelta">{{ translate('deIdaVuelta') }}</label>
            </div>

            <div class="form-input-wrapper" v-if="settings.modulo_tipo_vehiculos">
                <label class="form-label" for="public_tipo_veh">{{ translate('tipoVehiculo') }}</label>
                <select class="form-select" id="public_tipo_veh" v-model="tipoVehiculo" @change="resetEstimateDisplay">
                    <option v-for="c in availableCars" :value="c.id" :key="c.id">{{ $translateFromText(c.name) }}</option>
                </select>
            </div>
            <div class="form-input-wrapper" v-if="settings.modulo_tipo_servicios">
                <label class="form-label" for="public_tipo_srv">{{ translate('tipoServicioPasajero') }}</label>
                <select class="form-select" id="public_tipo_srv" v-model="tipoServicio" @change="resetEstimateDisplay">
                    <option v-for="s in availableServices" :value="s.id" :key="s.id">{{ $translateFromText(s.name) }}</option>
                </select>
            </div>
            <div class="form-input-wrapper" v-if="showBenefitSelector">
                <label class="form-label" for="public_tipo_beneficio">{{ translate('cotizacionBeneficioLabel') }}</label>
                <select class="form-select" id="public_tipo_beneficio" v-model="tipoBeneficioCotizacion" @change="syncSelectedFarePrice">
                    <option value="none">{{ translate('sinDescuentoBeneficio') }}</option>
                    <option value="residente">{{ translate('residente') }}</option>
                    <option value="jubilado">{{ translate('jubilado') }}</option>
                </select>
            </div>

            <div class="form-input-wrapper public-tariff-calc-wrap">
                <button
                    type="button"
                    class="btn btn-primary btn-lg btn-heavy public-tariff-calc-btn"
                    @click="onCalcularTarifa"
                    :disabled="!canCalculate || priceLoader"
                >
                    {{ translate('cotizacionCalcularTarifa') }}
                </button>
            </div>

            <div class="form-input-wrapper public-tariff-price" v-if="settings.modulo_precio_viaje">
                <template v-if="showDualFares && !priceLoader && hasCalculatedPrice">
                    <div class="public-tariff-fare-block">
                        <span class="public-tariff-fare-label">{{ selectedFareLabel }}</span>
                        <strong class="public-tariff-fare-amount">
                            <span v-lang.precioPrefijo></span>
                            {{ selectedFarePrice | numberFormatter }}
                            <span v-lang.precioSufijo></span>
                        </strong>
                    </div>
                    <p v-if="selectedBenefitHasDiscount" class="public-tariff-benefit-note">
                        {{ translate('descuentoResidenteJubiladoAplicado', { pct: settings.descuento_residente_jubilado }) }}
                    </p>
                </template>
                <template v-else-if="!(priceLoader) && tripEstimatedPrice > 0">
                    <div class="public-tariff-fare-block">
                        <span class="public-tariff-fare-label">
                            {{ !settings.precio_final ? translate('precioEstimado') : translate('precioFinal') }}
                        </span>
                        <strong class="public-tariff-fare-amount">
                            <span v-lang.precioPrefijo></span>
                            {{ tripEstimatedPrice | numberFormatter }}
                            <span v-lang.precioSufijo></span>
                        </strong>
                    </div>
                </template>
                <template v-else-if="!priceLoader">
                    <span class="public-tariff-fare-label">
                        {{ !settings.precio_final ? translate('precioEstimado') : translate('precioFinal') }}
                    </span>
                </template>
                <div
                    v-if="estimatedRouteDistanceMeters != null && !priceLoader && (hasCalculatedPrice || viaje_afuera)"
                    class="public-tariff-distance-line"
                >
                    {{ translate('estimacionDistancia') }}:
                    {{ (estimatedRouteDistanceMeters / 1000) | distanceConverter(settings.medidas_en_millas) | distanceFormatter(settings.medidas_en_millas, 'big') }}
                </div>
                <span v-if="viaje_afuera" class="text-warning">{{ translate('outOfRegion') }}</span>
                <span v-if="!viaje_afuera && !hasCalculatedPrice && !priceLoader && lastCalcAttempted" class="text-muted">
                    {{ translate('cotizacionSinPrecio') }}
                </span>
                <loader v-if="priceLoader" :size="16" :flag="true" :customAttribute="{tag: 'circle', children: 0, attribute:'stroke', value: '--main-color'}"></loader>
            </div>

            <div class="form-input-wrapper public-tariff-app-wrap" v-if="canOpenInApp">
                <button type="button" class="btn btn-primary btn-lg btn-heavy public-tariff-calc-btn" @click="openTripInApp">
                    {{ translate('cotizacionPedirEnApp') }}
                </button>
            </div>

            <div class="form-input-wrapper public-tariff-login-wrap">
                <button
                    v-if="user"
                    type="button"
                    class="btn btn-secondary public-tariff-login-btn"
                    @click="openTripLoggedIn"
                >
                    {{ translate('cotizacionPedirViaje') }}
                </button>
                <router-link
                    v-else
                    class="btn btn-secondary public-tariff-login-btn"
                    :to="{ name: 'login' }"
                    @click.native="persistCurrentQuoteIfReady"
                >
                    {{ translate('cotizacionLogin') }}
                </router-link>
            </div>
            </div>
            <p v-else-if="settings && !settings.modulo_precio_viaje" class="text-muted public-tariff-module-off" v-lang.moduloNoDisponible>
                Módulo no disponible.
            </p>
        </section>

        <modal v-if="showMapModal">
            <h3 slot="header">{{ mapModalTitle }}</h3>
            <div slot="body" class="public-tariff-map-modal">
                <div class="public-tariff-map-wrap">
                    <vue-maps
                        :center="mapModalCenter"
                        :zoom="15"
                        :allowToSelectPosition="true"
                        :points="[]"
                        :selectPositionIcon="mapPickerIcon"
                    />
                </div>
                <p class="public-tariff-map-hint">{{ translate('cotizacionMapaAyuda') }}</p>
            </div>
            <div slot="footer">
                <button type="button" class="btn btn-link" @click="closeMapPicker">{{ translate('cancelar') }}</button>
                <button type="button" class="btn btn-primary" @click="confirmMapPicker">{{ translate('cotizacionConfirmarUbicacion') }}</button>
            </div>
        </modal>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Geocoding } from '@/services/geocoding';
import { estimateTripTariff } from '@/services/tripTariff';
import { _parseGoogleResult } from '@/services/utility';
import dialogs from '@/services/dialogs';
import bus from '@/services/bus-event';
import { buildAppDownloadUrl, buildDeepLinkUrl, buildQuotePayload, savePendingTripQuote } from '@/services/tripQuoteBridge';

/* eslint-disable import/no-webpack-loader-syntax */
let mapPickerIcon = require('resources/default/img/user.png');
/* eslint-enable import/no-webpack-loader-syntax */

export default {
    name: 'public-tariff-estimate',
    data () {
        return {
            requestPosition: null,
            destinationAddress: null,
            originDescription: '',
            destDescription: '',
            originRemountKey: 0,
            destRemountKey: 0,
            tripEstimatedPrice: 0,
            tripEstimatedPriceResidente: 0,
            tripEstimatedPriceNoResidente: 0,
            estimatedRouteDistanceMeters: null,
            precioSinDescuento: 0,
            priceLoader: false,
            viaje_afuera: false,
            esIdaVuelta: false,
            tipoVehiculo: 1,
            tipoServicio: 1,
            tipoBeneficioCotizacion: 'none',
            lastCalcAttempted: false,
            showMapModal: false,
            mapPickerTarget: null,
            mapModalCenter: { lat: -34.6, lng: -58.4 },
            pendingMapPoint: null,
            busMapHandler: null,
            appFallbackTimer: null,
            mapPickerIcon: mapPickerIcon
        };
    },
    computed: {
        ...mapGetters({
            settings: 'company/config',
            user: 'auth/user'
        }),
        originReady () {
            return !!(this.requestPosition && this.requestPosition.location);
        },
        destReady () {
            return !!(this.destinationAddress && this.destinationAddress.location);
        },
        canCalculate () {
            return this.originReady && this.destReady && this.areServiceCompatible();
        },
        showDualFares () {
            return !!(
                this.settings &&
                this.settings.modulo_descuento_residente_jubilado &&
                parseFloat(this.settings.descuento_residente_jubilado) > 0
            );
        },
        showBenefitSelector () {
            return this.showDualFares;
        },
        selectedBenefitHasDiscount () {
            return this.tipoBeneficioCotizacion === 'residente' || this.tipoBeneficioCotizacion === 'jubilado';
        },
        selectedFarePrice () {
            if (!this.showDualFares) {
                return this.tripEstimatedPrice;
            }
            return this.selectedBenefitHasDiscount ? this.tripEstimatedPriceResidente : this.tripEstimatedPriceNoResidente;
        },
        selectedFareLabel () {
            if (this.tipoBeneficioCotizacion === 'residente') {
                return this.translate('cotizacionTarifaResidente');
            }
            if (this.tipoBeneficioCotizacion === 'jubilado') {
                return this.translate('cotizacionTarifaJubilado');
            }
            return this.translate('cotizacionTarifaNoResidente');
        },
        hasCalculatedPrice () {
            if (this.showDualFares) {
                return this.selectedFarePrice > 0;
            }
            return this.tripEstimatedPrice > 0;
        },
        canOpenInApp () {
            return this.hasCalculatedPrice && !this.viaje_afuera && !this.priceLoader && this.originReady && this.destReady;
        },
        mapModalTitle () {
            if (this.mapPickerTarget === 'dest') {
                return this.translate('cotizacionMapaTituloDestino');
            }
            return this.translate('cotizacionMapaTituloOrigen');
        },
        availableCars () {
            if (this.settings && this.settings.modulo_tipo_vehiculos && this.settings.tiposVehiculo && this.settings.tiposVehiculo.length) {
                return this.settings.tiposVehiculo.filter(c => c.active);
            }
            return [];
        },
        availableServices () {
            if (this.settings && this.settings.modulo_tipo_servicios && this.settings.tiposServicio && this.settings.tiposServicio.length) {
                return this.settings.tiposServicio.filter(c => c.active);
            }
            return [];
        }
    },
    mounted () {
        this.language = this.$getLang();
        this.$store.dispatch('company/getConfig').then(() => {
            this.seedGeolocationBias();
        });
    },
    beforeDestroy () {
        this.clearAppFallbackTimer();
        this.closeMapPicker();
    },
    methods: {
        getGeocoding () {
            let runtime = (this.settings && this.settings.modulo_geocoding) || 'google';
            return new Geocoding(runtime);
        },
        seedGeolocationBias () {
            if (!this.settings || !this.settings.zonas || !this.settings.zonas.length) {
                return;
            }
            const z = this.settings.zonas[0];
            if (!this.$store.getters['geolocation/actual']) {
                this.$store.commit('geolocation/SET_POSITION', { lat: z.lat, lng: z.lng });
                this.$store.commit('geolocation/LOCATION_ENABLED_SET', true);
            }
        },
        defaultMapCenter () {
            if (this.settings && this.settings.zonas && this.settings.zonas[0]) {
                return { lat: this.settings.zonas[0].lat, lng: this.settings.zonas[0].lng };
            }
            const g = this.$store.getters['geolocation/actual'];
            if (g) {
                return { lat: g.lat, lng: g.lng };
            }
            return { lat: -34.6, lng: -58.4 };
        },
        makeLatLngCompat (lat, lng) {
            return {
                lat: function () { return lat; },
                lng: function () { return lng; }
            };
        },
        extractLatLng (addressLike) {
            if (!addressLike || !addressLike.location) {
                return null;
            }
            const loc = addressLike.location;
            const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
            const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
            if (typeof lat !== 'number' || typeof lng !== 'number') {
                return null;
            }
            return { lat, lng };
        },
        descriptionFromParsed (p) {
            if (!p) {
                return '';
            }
            if (p.description) {
                return p.description;
            }
            let d = '';
            if (p.calle) {
                d = p.numero > 0 ? `${p.calle} ${p.numero}` : p.calle;
            }
            if (p.barrio) {
                d += (d ? ', ' : '') + p.barrio;
            }
            return d || this.translate('cotizacionTitulo');
        },
        applyGeocodeFromLatLng (lat, lng) {
            const geocoding = this.getGeocoding();
            if (geocoding._runtime === 'google') {
                return new Promise((resolve, reject) => {
                    /* eslint-disable no-undef */
                    if (typeof google === 'undefined' || !google.maps) {
                        reject(new Error('Google Maps not available'));
                        return;
                    }
                    const geocoder = new google.maps.Geocoder();
                    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                        if (status === 'OK' && results[0]) {
                            const parsed = _parseGoogleResult(results[0]);
                            parsed.description = results[0].formatted_address;
                            parsed.location = results[0].geometry.location;
                            resolve(parsed);
                        } else {
                            reject(new Error('Geocode failed'));
                        }
                    });
                });
            }
            return geocoding.reverse({ lat, lng }).then((parsed) => {
                const p = parsed || {};
                p.description = this.descriptionFromParsed(p);
                p.location = this.makeLatLngCompat(lat, lng);
                return p;
            });
        },
        resetEstimateDisplay () {
            this.tripEstimatedPrice = 0;
            this.tripEstimatedPriceResidente = 0;
            this.tripEstimatedPriceNoResidente = 0;
            this.estimatedRouteDistanceMeters = null;
            this.precioSinDescuento = 0;
            this.viaje_afuera = false;
            this.lastCalcAttempted = false;
        },
        syncSelectedFarePrice () {
            if (this.showDualFares && (this.tripEstimatedPriceResidente > 0 || this.tripEstimatedPriceNoResidente > 0)) {
                this.tripEstimatedPrice = this.selectedFarePrice;
            }
        },
        onOriginChange (result) {
            this.requestPosition = {
                location: {
                    lat: result.location.lat(),
                    lng: result.location.lng()
                }
            };
            this.originDescription = result.description || '';
            this.resetEstimateDisplay();
        },
        onDestinationChange (result) {
            this.destinationAddress = result;
            this.destDescription = result.description || '';
            this.resetEstimateDisplay();
        },
        swapOriginDestination () {
            const currentOrigin = this.extractLatLng(this.requestPosition);
            const currentDest = this.extractLatLng(this.destinationAddress);
            const oldOriginDescription = this.originDescription;
            const oldDestDescription = this.destDescription;

            this.requestPosition = currentDest ? { location: { lat: currentDest.lat, lng: currentDest.lng } } : null;
            this.destinationAddress = currentOrigin
                ? { description: oldOriginDescription || '', location: { lat: currentOrigin.lat, lng: currentOrigin.lng } }
                : null;

            this.originDescription = oldDestDescription || '';
            this.destDescription = oldOriginDescription || '';
            this.originRemountKey++;
            this.destRemountKey++;
            this.resetEstimateDisplay();
        },
        onCalcularTarifa () {
            if (!this.areServiceCompatible()) {
                dialogs.message(this.translate('serviceIncompatible'), { duration: 6, estado: 'error' });
                return;
            }
            if (!this.canCalculate) {
                return;
            }
            this.lastCalcAttempted = true;
            this.runEstimate();
        },
        areServiceCompatible () {
            if (this.settings.modulo_tipo_vehiculos && this.settings.modulo_tipo_servicios) {
                if (this.settings.tiposVehiculo && this.settings.tiposVehiculo.length && this.settings.tiposServicio && this.settings.tiposServicio.length) {
                    let veh = this.settings.tiposVehiculo.find(v => v.id === this.tipoVehiculo);
                    if (veh && veh.servicios && veh.servicios.length) {
                        let serv = veh.servicios.find(s => s.id === this.tipoServicio);
                        return !!serv;
                    }
                    return false;
                }
            }
            return true;
        },
        async runEstimate () {
            if (!this.settings.modulo_precio_viaje) {
                return;
            }
            this.priceLoader = true;
            this.viaje_afuera = false;
            this.estimatedRouteDistanceMeters = null;
            const geocoding = this.getGeocoding();
            const dest = this.destinationAddress.location;
            const destLat = typeof dest.lat === 'function' ? dest.lat() : dest.lat;
            const destLng = typeof dest.lng === 'function' ? dest.lng() : dest.lng;

            let result;
            try {
                result = await geocoding.calculateRoute(
                    this.requestPosition.location,
                    { lat: destLat, lng: destLng }
                );
            } catch (e) {
                console.error(e);
                dialogs.message(this.translate('errorCalculandoPrecio'), { duration: 10, estado: 'error' });
                this.tripEstimatedPrice = 0;
                this.estimatedRouteDistanceMeters = null;
                this.priceLoader = false;
                return;
            }

            this.estimatedRouteDistanceMeters = result.distanceValue;

            try {
                const payload = {
                    distance_meters: result.distanceValue,
                    duration_seconds: result.timeValue,
                    origin_lat: this.requestPosition.location.lat,
                    origin_lng: this.requestPosition.location.lng,
                    dest_lat: destLat,
                    dest_lng: destLng,
                    es_ida_vuelta: this.esIdaVuelta,
                    vehicle_type_id: this.tipoVehiculo,
                    service_type_id: this.tipoServicio,
                    payment_method_id: 0
                };
                if (this.showDualFares) {
                    const [dataNoResidente, dataResidente] = await Promise.all([
                        estimateTripTariff({ ...payload, aplicar_descuento_residente: false }),
                        estimateTripTariff({ ...payload, aplicar_descuento_residente: true })
                    ]);
                    this.viaje_afuera = !!dataNoResidente.viaje_afuera || !!dataResidente.viaje_afuera;
                    this.tripEstimatedPriceNoResidente = this.parseFareAmount(dataNoResidente.price);
                    this.tripEstimatedPriceResidente = this.parseFareAmount(dataResidente.price);
                    this.tripEstimatedPrice = this.selectedFarePrice;
                    this.precioSinDescuento = 0;
                } else {
                    const data = await estimateTripTariff({ ...payload, aplicar_descuento_residente: false });
                    this.viaje_afuera = !!data.viaje_afuera;
                    this.precioSinDescuento = 0;
                    this.tripEstimatedPrice = this.parseFareAmount(data.price);
                    this.tripEstimatedPriceResidente = 0;
                    this.tripEstimatedPriceNoResidente = 0;
                }
            } catch (err) {
                console.error(err);
                dialogs.message(this.translate('errorCalculandoPrecio'), { duration: 10, estado: 'error' });
                this.tripEstimatedPrice = 0;
                this.tripEstimatedPriceResidente = 0;
                this.tripEstimatedPriceNoResidente = 0;
                this.precioSinDescuento = 0;
                this.estimatedRouteDistanceMeters = null;
            } finally {
                this.priceLoader = false;
            }
        },
        parseFareAmount (value) {
            return typeof value === 'number' ? value : parseFloat(value) || 0;
        },
        currentQuotePayload () {
            return buildQuotePayload(this.requestPosition, this.destinationAddress, {
                origin_desc: this.originDescription,
                dest_desc: this.destDescription,
                es_ida_vuelta: this.esIdaVuelta,
                tipo_vehiculo: this.tipoVehiculo,
                tipo_servicio: this.tipoServicio,
                tipo_beneficio: this.selectedBenefitHasDiscount ? this.tipoBeneficioCotizacion : ''
            });
        },
        persistCurrentQuote () {
            const quote = this.currentQuotePayload();
            if (quote) {
                savePendingTripQuote(quote);
            }
            return quote;
        },
        openTripInApp () {
            const quote = this.persistCurrentQuote();
            if (!quote) {
                return;
            }
            if (process.env.CORDOVA) {
                this.$router.push({ name: 'home' });
                return;
            }
            this.openDeepLinkWithStoreFallback(buildDeepLinkUrl(quote));
        },
        clearAppFallbackTimer () {
            if (this.appFallbackTimer) {
                clearTimeout(this.appFallbackTimer);
                this.appFallbackTimer = null;
            }
        },
        openDeepLinkWithStoreFallback (deepLinkUrl) {
            const downloadUrl = buildAppDownloadUrl();
            let appOpened = false;
            const cleanup = () => {
                appOpened = true;
                this.clearAppFallbackTimer();
                document.removeEventListener('visibilitychange', onVisibilityChange);
                window.removeEventListener('pagehide', cleanup);
            };
            const onVisibilityChange = () => {
                if (document.hidden) {
                    cleanup();
                }
            };
            document.addEventListener('visibilitychange', onVisibilityChange);
            window.addEventListener('pagehide', cleanup);
            this.clearAppFallbackTimer();
            this.appFallbackTimer = setTimeout(() => {
                if (!appOpened) {
                    cleanup();
                    window.location.href = downloadUrl;
                }
            }, 1600);
            window.location.href = deepLinkUrl;
        },
        openTripLoggedIn () {
            if (!this.persistCurrentQuote()) {
                return;
            }
            this.$router.push({ name: 'home' });
        },
        persistCurrentQuoteIfReady () {
            if (this.canOpenInApp) {
                this.persistCurrentQuote();
            }
        },
        useMyLocationFor (which) {
            if (!navigator.geolocation) {
                dialogs.message(this.translate('conectionError'), { duration: 6, estado: 'error' });
                return;
            }
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const lat = pos.coords.latitude;
                    const lng = pos.coords.longitude;
                    this.applyGeocodeFromLatLng(lat, lng).then((parsed) => {
                        if (which === 'origin') {
                            this.requestPosition = { location: { lat, lng } };
                            this.originDescription = parsed.description || this.descriptionFromParsed(parsed);
                            this.originRemountKey++;
                            this.$store.commit('geolocation/SET_POSITION', { lat, lng });
                        } else {
                            this.destinationAddress = parsed;
                            this.destDescription = parsed.description || this.descriptionFromParsed(parsed);
                            this.destRemountKey++;
                        }
                        this.resetEstimateDisplay();
                    }).catch(() => {
                        dialogs.message(this.translate('errorCalculandoPrecio'), { duration: 6, estado: 'error' });
                    });
                },
                () => {
                    dialogs.message(this.translate('conectionError'), { duration: 6, estado: 'error' });
                },
                { enableHighAccuracy: true, timeout: 15000 }
            );
        },
        openMapPicker (target) {
            if (this.busMapHandler) {
                bus.off('map_point_selected', this.busMapHandler);
                this.busMapHandler = null;
            }
            this.mapPickerTarget = target;
            let center = this.defaultMapCenter();
            if (target === 'origin' && this.requestPosition && this.requestPosition.location) {
                center = { lat: this.requestPosition.location.lat, lng: this.requestPosition.location.lng };
            } else if (target === 'dest' && this.destinationAddress && this.destinationAddress.location) {
                const d = this.destinationAddress.location;
                center = {
                    lat: typeof d.lat === 'function' ? d.lat() : d.lat,
                    lng: typeof d.lng === 'function' ? d.lng() : d.lng
                };
            }
            this.mapModalCenter = center;
            this.pendingMapPoint = { lat: center.lat, lng: center.lng };
            this.showMapModal = true;
            this.$nextTick(() => {
                this.busMapHandler = (pos) => {
                    if (pos && pos.location) {
                        this.pendingMapPoint = { lat: pos.location.lat, lng: pos.location.lng };
                    }
                };
                bus.on('map_point_selected', this.busMapHandler);
                setTimeout(() => {
                    bus.emit('set_center', this.mapModalCenter);
                }, 400);
            });
        },
        closeMapPicker () {
            if (this.busMapHandler) {
                bus.off('map_point_selected', this.busMapHandler);
                this.busMapHandler = null;
            }
            this.showMapModal = false;
            this.mapPickerTarget = null;
        },
        confirmMapPicker () {
            const target = this.mapPickerTarget;
            const loc = this.pendingMapPoint || this.mapModalCenter;
            const lat = loc.lat;
            const lng = loc.lng;
            this.applyGeocodeFromLatLng(lat, lng).then((parsed) => {
                if (target === 'origin') {
                    this.requestPosition = { location: { lat, lng } };
                    this.originDescription = parsed.description || this.descriptionFromParsed(parsed);
                    this.originRemountKey++;
                } else if (target === 'dest') {
                    this.destinationAddress = parsed;
                    this.destDescription = parsed.description || this.descriptionFromParsed(parsed);
                    this.destRemountKey++;
                }
                this.resetEstimateDisplay();
                this.closeMapPicker();
            }).catch(() => {
                dialogs.message(this.translate('errorCalculandoPrecio'), { duration: 6, estado: 'error' });
            });
        }
    }
};
</script>

<style scoped>
.public-tariff-page {
    min-height: 100vh;
    box-sizing: border-box;
    background: var(--login-background);
    display: flex;
    flex-direction: column;
}

/* Misma idea que login: solo logo sobre el verde de marca */
.public-tariff-hero {
    flex-shrink: 0;
    padding: 2em 1em 1.5em;
    text-align: center;
}

.public-tariff-hero .public-tariff-logo {
    max-width: 300px;
    width: 100%;
    display: block;
    margin: 0 auto;
    opacity: var(--login-logo-opacity, 1);
    height: var(--login-logo-height, auto);
}

/* Tarjeta blanca: redondeo solo arriba, título + intro + formulario */
.public-tariff-sheet {
    flex: 1 1 auto;
    width: 100%;
    max-width: 520px;
    margin: 0 auto;
    padding: 1.35rem 1.25rem 2rem;
    background: #fff;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.07);
    box-sizing: border-box;
}

.public-tariff-sheet-head {
    margin-bottom: 1rem;
    padding-bottom: 0.15rem;
}

.public-tariff-title {
    font-size: 1.35rem;
    font-weight: 600;
    margin: 0 0 0.5rem;
    color: var(--text-color, #222);
}

.public-tariff-intro {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.45;
    opacity: 0.88;
    color: var(--text-softer-color, #555);
}

.public-tariff-body {
    padding-top: 0.15rem;
}

.public-tariff-module-off {
    margin: 0;
    padding: 0.25rem 0 0.5rem;
}

.public-tariff-field-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.35rem 0.75rem;
    margin-bottom: 1rem;
}

.public-tariff-field-main {
    flex: 1 1 220px;
    min-width: 0;
}

.public-tariff-field-actions {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex-shrink: 0;
    padding-bottom: 3px;
}

.public-tariff-action-btn {
    font-size: 0.85rem;
    font-weight: 500;
    padding: 0.2rem 0.35rem;
    min-height: 0;
    line-height: 1.2;
    text-decoration: none;
}

.public-tariff-action-map {
    opacity: 0.92;
}

.public-tariff-label-row {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}

.public-tariff-swap-inline-btn {
    border: 0;
    background: transparent;
    box-shadow: none;
    outline: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.1rem 0.2rem;
    line-height: 1;
    cursor: pointer;
    margin-bottom: 5px;
}

.public-tariff-swap-icon {
    font-size: 1.5rem;
    line-height: 1;
}

.public-tariff-calc-wrap {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
}

.public-tariff-calc-btn {
    width: 100%;
}

.public-tariff-price {
    margin-top: 1rem;
    text-align: center;
}

.public-tariff-fare-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
}

.public-tariff-fare-label {
    font-size: 1.15rem;
    font-weight: 600;
    color: var(--text-softer-color, #555);
}

.public-tariff-fare-amount {
    display: block;
    font-size: clamp(2.75rem, 12vw, 4.25rem);
    font-weight: 700;
    line-height: 1.05;
    letter-spacing: -0.02em;
    color: var(--text-color, #222);
    font-variant-numeric: tabular-nums;
}

.public-tariff-benefit-note {
    margin: 0.5rem 0 0;
    font-size: 0.95rem;
    color: var(--text-softer-color, #555);
}

.public-tariff-app-wrap {
    margin-top: 0.75rem;
}

.public-tariff-login-wrap {
    margin-top: 1rem;
}

.public-tariff-login-wrap .btn,
.public-tariff-login-btn {
    width: 100%;
}

.public-tariff-distance-line {
    display: block;
    margin-top: 0.65rem;
    font-size: 1.05rem;
    font-weight: 500;
    color: var(--text-softer-color, #555);
}

.public-tariff-padding {
    padding: 1rem;
}

.public-tariff-map-modal {
    max-width: 100%;
}

.public-tariff-map-wrap {
    height: 260px;
    width: 100%;
    border-radius: 4px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.08);
}

.public-tariff-map-hint {
    font-size: 0.85rem;
    margin: 0.75rem 0 0;
    line-height: 1.35;
    opacity: 0.85;
}
</style>

<style scoped>
/* Map embed: override full-viewport height from Maps.vue */
.public-tariff-map-wrap >>> .vue-map-container {
    height: 260px !important;
    min-height: 260px;
}
.public-tariff-map-wrap >>> .vue-map--wrapper {
    height: 260px !important;
    min-height: 260px;
}
</style>



// WEBPACK FOOTER //
// src/components/views/PublicTariffEstimate.vue